import { useEffect, useRef, useState } from 'react';
import type { RecurringRevenueReport } from '@vy/types';
import type { FilterLineType } from '../../../../components/new-design/vyTableFilter';
import { runRecurringRevenueAggregation } from '../workers/recurring-revenue-aggregation-shared';
import type {
  RecurringRevenueAggregationBundle,
  RecurringRevenueAggregationRequest,
  RecurringRevenueAggregationResponse,
} from '../workers/recurring-revenue-aggregation-types';

type Params = {
  report: RecurringRevenueReport;
  filters: FilterLineType[];
};

type Result = {
  bundle: RecurringRevenueAggregationBundle | null;
  error: string | null;
  isAggregating: boolean;
};

let workerSupported = typeof Worker !== 'undefined';

// Heavy expansion + per-month metric computation runs in a Web Worker so the
// main thread stays free for paint, scroll, and input.
export function useRecurringRevenueAggregations({ report, filters }: Params): Result {
  const workerRef = useRef<Worker | null>(null);
  const requestIdRef = useRef(0);
  const lastAppliedRef = useRef(0);
  const [bundle, setBundle] = useState<RecurringRevenueAggregationBundle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isAggregating, setIsAggregating] = useState(true);

  useEffect(() => {
    if (!workerSupported) return;

    let worker: Worker | null = null;
    try {
      worker = new Worker(
        new URL('../workers/recurring-revenue-aggregation.worker.ts', import.meta.url),
        { type: 'module' },
      );
      workerRef.current = worker;
    } catch (err) {
      console.warn('[recurring-revenue] worker init failed; using main-thread fallback', err);
      workerSupported = false;
      return;
    }

    const onMessage = (event: MessageEvent<RecurringRevenueAggregationResponse>) => {
      const data = event.data;
      if (data.requestId < lastAppliedRef.current) return;
      lastAppliedRef.current = data.requestId;

      if ('error' in data) {
        setError(data.error);
        setIsAggregating(false);
      } else {
        setBundle(data);
        setError(null);
        setIsAggregating(false);
      }
    };

    worker.addEventListener('message', onMessage);

    return () => {
      worker?.removeEventListener('message', onMessage);
      worker?.terminate();
      workerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setIsAggregating(true);

    const request: RecurringRevenueAggregationRequest = { requestId, report, filters };

    if (workerRef.current) {
      workerRef.current.postMessage(request);
      return;
    }

    try {
      const result = runRecurringRevenueAggregation(request);
      if (result.requestId >= lastAppliedRef.current) {
        lastAppliedRef.current = result.requestId;
        setBundle(result);
        setError(null);
        setIsAggregating(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setIsAggregating(false);
    }
  }, [report, filters]);

  return { bundle, error, isAggregating };
}
